/* istanbul ignore file */

import moment from 'moment';
// eslint-disable-next-line
import momentTz from 'moment-timezone';
// eslint-disable-next-line
import momentDurationFormatSetup from 'moment-duration-format';
import _ from 'lodash';
import {
  MOMENT,
  SHORT_DATE_FORMAT,
  SEARCH_DATE_FORMAT
} from 'common/constants/constants';

export const TIME_UNITS = {
  seconds: 1,
  minutes: 2,
  hours: 3,
  days: 4,
  weeks: 5,
  months: 6,
  years: 7
};

const resolveDuration = (numOfDecimal, duration) =>
  numOfDecimal > 0 ? duration.toFixed(numOfDecimal) : _.round(duration);

export const convertMinutesToDisplayableTime = ({
  minutes = 0,
  numOfDecimal = 0,
  maxTimeUnit = TIME_UNITS.months
}) => {
  const duration = moment.duration(minutes, MOMENT.UNITS.MINUTE);
  const months = duration.asMonths();
  const days = duration.asDays();
  const hours = duration.asHours();
  switch (true) {
    case months >= 2 && maxTimeUnit >= TIME_UNITS.months:
      return {
        value: resolveDuration(numOfDecimal, months),
        label: 'Months',
        type: 'M',
        roundedInMinutes: 30 * 24 * 60 * _.round(months)
      };

    case days >= 2 && maxTimeUnit >= TIME_UNITS.days:
      return {
        value: resolveDuration(numOfDecimal, days),
        label: 'Days',
        type: 'd',
        roundedInMinutes: 24 * 60 * _.round(days)
      };

    case hours >= 2 && maxTimeUnit >= TIME_UNITS.hours:
      return {
        value: resolveDuration(numOfDecimal, hours),
        label: 'Hours',
        type: 'h',
        roundedInMinutes: 60 * _.round(hours)
      };
    default:
      return {
        value: _.round(minutes),
        label: 'Minutes',
        type: 'm',
        roundedInMinutes: _.round(minutes)
      };
  }
};

export const formatStartEndDate = (start, end) => {
  const startDate = moment(start);
  const endDate = moment(end);
  const isSameMonth = moment(startDate).isSame(endDate, 'month');
  const dayAndMonth = `${startDate.format(
    SHORT_DATE_FORMAT
  )} - ${endDate.format(isSameMonth ? 'D' : SHORT_DATE_FORMAT)}`;

  return `${dayAndMonth}, ${endDate.format('YYYY')}`;
};

export const isValidDate = date => {
  return date instanceof Date && !isNaN(date);
};

export const getDefaultFormatter = ({
  minutes = 0,
  minutesUnit = 'm',
  hoursUnit = 'h',
  daysUnit = 'd',
  yearsUnit = 'y'
}) => {
  const oneDayInMinutes = 60 * 24;
  const oneYearInMinutes = 365 * 60 * 24;

  let format;
  if (minutes > oneYearInMinutes) format = 'Y[y], D[d]';
  else if (minutes > oneDayInMinutes) format = 'D[d], h[h]';
  else format = 'h[h], m[m]';
  return format
    .replace('[d]', `[${daysUnit}]`)
    .replace('[m]', `[${minutesUnit}]`)
    .replace('[h]', `[${hoursUnit}]`)
    .replace('[y]', `[${yearsUnit}]`);
};

export const momentDateValidator = dateStr => {
  if (!dateStr) {
    return moment();
  }
  const momentDate = moment(dateStr);
  if (!momentDate || !momentDate.isValid()) {
    return moment();
  }
  return momentDate;
};

export const calculateDateRangeBasedOnRetentionAndMaxRetention = (
  retentionDays,
  maxRetentionDate,
  startDate,
  endDate
) => {
  const startDateValidated = momentDateValidator(startDate);
  const endDateValidated = momentDateValidator(endDate);

  const maxRetentionDateFormatted = moment(maxRetentionDate);
  const retentionDateFormatted = moment()
    .subtract(retentionDays, 'day')
    .startOf('day');

  const firstRetentionDate = retentionDays
    ? moment.max([maxRetentionDateFormatted, retentionDateFormatted]) // if both retentionDay and maxRetentionDate - get max date
    : maxRetentionDateFormatted;

  const retentionStartDate = moment.max([
    firstRetentionDate,
    startDateValidated
  ]);

  const retentionEndDate = moment(firstRetentionDate).isAfter(endDateValidated)
    ? moment()
    : endDateValidated;

  return {
    retentionDays: moment().diff(firstRetentionDate, 'days'),
    retentionStartDate: retentionStartDate.format(SEARCH_DATE_FORMAT),
    retentionEndDate: retentionEndDate.format(SEARCH_DATE_FORMAT)
  };
};

export const getRangeOfPastXMonths = (x = 1) => {
  const start_date = moment().subtract(x, 'month').format('YYYY-MM-DD');
  const end_date = moment().format('YYYY-MM-DD');
  return { start_date, end_date };
};
